import { TabletButton } from '@campfire/tablet-button';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ReCAPTCHA from 'react-google-recaptcha';
import { GetPublicForm_vm_form as PublicForm } from './__generated__/GetPublicForm';
import { FormikErrorFocus } from '../../../common/form/FormikErrorFocus';
import { TaskFormFieldList } from '../../../common/form/task-form/TaskFormFieldList';
import { TaskFieldContainerProvider } from '../../../common/form/task-form/TaskFieldContainerContext';
import { FormFieldContainer } from '../../../common/inputs/containers/FormFieldContainer';
import { initTaskFormFormikValues, TaskFormValuesType } from '../../../common/form/task-form/model/task-form-init';
import { splitTaskItemsIntoPages, TASK_ITEM_PAGE_BREAK } from '../../../common/form/task-form/paged-task-items';
import { FormPageNav } from '../../../common/form/task-form/FormPageNav';
import { dynamicValidationSchema } from '../../../common/form/task-form/model/validation-schema';
import { deserializeTaskItems } from '../../admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import { useEndpointFetch } from '../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../global/config/useSnackbar';
import { useSession } from '../../../global/auth/useSession';

export type RepeatableGroupInstance = {
  groupInstanceId: string;
  values: TaskFormValuesType;
};

export type RepeatableGroupFormikValue = {
  instances: RepeatableGroupInstance[];
  groupFormItemId: string;
  title: string;
  description: string | null;
  label: string;
  minInstances: number;
  maxInstances: number;
  groupTaskItems: any[];
};

const useStyles = makeStyles({
  formFieldsWrapper: {
    '& .form-field-input': {
      '& input, & textarea': {
        padding: '12px 0',
      },
    },
    '& .form-field-outline': {
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderRadius: 0,
      borderBottom: '2px solid #e0e0e0',
      transition: 'border-bottom-color 0.2s',
    },
    '& .MuiOutlinedInput-root:hover .form-field-outline': {
      borderBottom: '2px solid #bdbdbd',
    },
    '& .MuiOutlinedInput-root.Mui-focused .form-field-outline': {
      borderBottom: '2px solid #6366f1',
    },
  },
});

interface PublicFormViewProps {
  form: PublicForm;
  onSubmitSuccess: () => void;
}

export const PublicFormView: React.FC<PublicFormViewProps> = ({ form, onSubmitSuccess }) => {
  const classes = useStyles();
  const { user } = useSession();
  const submitFormFetch = useEndpointFetch('/vm/form/submit', { withAuth: false });
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const { setSnackbar } = useSnackbar();
  const [recaptchaRef] = useState<React.RefObject<any>>(React.createRef());

  const taskItems = useMemo(() => {
    const sortedFormItems = [...form.formItems].sort((a, b) => (a.order > b.order ? 1 : -1));

    // Transform nested trigger item references to use taskItemId
    const transformTriggerItems = (triggerItems: any[]) => {
      if (!triggerItems) return triggerItems;
      return triggerItems.map((triggerItem: any) => ({
        ...triggerItem,
        item: triggerItem.item
          ? {
              ...triggerItem.item,
              taskItemId: triggerItem.item.formItemId,
            }
          : triggerItem.item,
      }));
    };

    // Transform options with triggers
    const transformOptions = (options: any[]) => {
      if (!options) return options;
      return options.map((option) => ({
        ...option,
        trigger: option.trigger
          ? {
              ...option.trigger,
              items: transformTriggerItems(option.trigger.items),
            }
          : option.trigger,
      }));
    };

    // Transform field from Form type to Task type
    const transformField = (field: any): any => {
      if (!field) return field;

      if (field.__typename === 'VOLUNTEER_FormDropdownTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_DropdownTaskFieldType',
          dropdownOptions: transformOptions(field.dropdownOptions),
        };
      }
      if (field.__typename === 'VOLUNTEER_FormCheckboxTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_CheckboxTaskFieldType',
          checkboxOptions: transformOptions(field.checkboxOptions),
        };
      }
      if (field.__typename === 'VOLUNTEER_FormMultipleChoiceTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_MultipleChoiceTaskFieldType',
          multipleChoiceOptions: transformOptions(field.multipleChoiceOptions),
        };
      }
      if (field.__typename === 'VOLUNTEER_FormQuizTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_QuizTaskFieldType',
          quizTaskFieldOptions: transformOptions(field.quizTaskFieldOptions),
        };
      }
      if (field.__typename === 'VOLUNTEER_FormMatrixTaskFieldType') {
        return {
          ...field,
          __typename: 'VOLUNTEER_MatrixTaskFieldType',
        };
      }
      return field;
    };

    // Transform form items from Form types to Task types
    // triggerItem is preserved via spread operator for byTriggerOption filtering
    return sortedFormItems.map((formItem) => {
      if (formItem.__typename === 'VOLUNTEER_FormItemFieldType') {
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemFieldType' as const,
          taskItemId: formItem.formItemId,
          field: transformField(formItem.field),
          optional: formItem.optional,
        };
      }
      if (formItem.__typename === 'VOLUNTEER_FormItemHeadingType') {
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemHeadingType' as const,
          taskItemId: formItem.formItemId,
        };
      }
      if (formItem.__typename === 'VOLUNTEER_FormItemContentType') {
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemContentType' as const,
          taskItemId: formItem.formItemId,
          attachments: [],
        };
      }
      if (formItem.__typename === 'VOLUNTEER_FormItemMediaType') {
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemMediaType' as const,
          taskItemId: formItem.formItemId,
        };
      }
      if (formItem.__typename === 'VOLUNTEER_FormItemRepeatableGroupType') {
        const groupChildItems = ((formItem as any).groupItems || [])
          .sort((a: any, b: any) => (a.order > b.order ? 1 : -1))
          .map((childItem: any) => {
            if (childItem.__typename === 'VOLUNTEER_FormItemFieldType') {
              return {
                ...childItem,
                __typename: 'VOLUNTEER_TaskItemFieldType' as const,
                taskItemId: childItem.formItemId,
                field: transformField(childItem.field),
                optional: childItem.optional,
              };
            }
            if (childItem.__typename === 'VOLUNTEER_FormItemHeadingType') {
              return {
                ...childItem,
                __typename: 'VOLUNTEER_TaskItemHeadingType' as const,
                taskItemId: childItem.formItemId,
              };
            }
            if (childItem.__typename === 'VOLUNTEER_FormItemContentType') {
              return {
                ...childItem,
                __typename: 'VOLUNTEER_TaskItemContentType' as const,
                taskItemId: childItem.formItemId,
                attachments: [],
              };
            }
            return childItem;
          });
        return {
          ...formItem,
          __typename: 'VOLUNTEER_TaskItemRepeatableGroupType' as const,
          taskItemId: formItem.formItemId,
          title: (formItem as any).title || (formItem as any).label || 'Section',
          description: (formItem as any).description || null,
          label: (formItem as any).label || 'item',
          minInstances: (formItem as any).minInstances ?? 1,
          maxInstances: (formItem as any).maxInstances ?? 10,
          groupItems: groupChildItems,
        };
      }
      if (formItem.__typename === 'VOLUNTEER_FormItemPageBreakType') {
        return {
          ...formItem,
          __typename: TASK_ITEM_PAGE_BREAK,
          taskItemId: formItem.formItemId,
          title: (formItem as any).title || null,
          description: (formItem as any).description || null,
        };
      }
      return formItem as any;
    });
  }, [form.formItems]);

  const pages = useMemo(() => splitTaskItemsIntoPages(taskItems as any[]), [taskItems]);

  useEffect(() => {
    setCurrentPage(0);
  }, [form.formId]);

  const currentPageItems = pages[currentPage]?.items ?? [];

  const initialFormikValues = useMemo(() => {
    if (taskItems.length === 0) return undefined;
    const baseValues = initTaskFormFormikValues(taskItems as any, undefined);

    // Initialize repeatable group values
    taskItems.forEach((item: any) => {
      if (item.__typename === 'VOLUNTEER_TaskItemRepeatableGroupType') {
        const childFieldItems = item.groupItems.filter(
          (child: any) => child.__typename === 'VOLUNTEER_TaskItemFieldType'
        );
        const instanceValues = initTaskFormFormikValues(childFieldItems, undefined);
        const instances: RepeatableGroupInstance[] = Array.from({ length: item.minInstances ?? 1 }, () => ({
          groupInstanceId: uuidv4(),
          values: { ...instanceValues },
        }));
        baseValues[`__rg_${item.taskItemId}`] = {
          instances,
          groupFormItemId: item.taskItemId,
          title: item.title || item.label,
          description: item.description || null,
          label: item.label,
          minInstances: item.minInstances,
          maxInstances: item.maxInstances,
          groupTaskItems: item.groupItems,
        } as any;
      }
    });

    return baseValues;
  }, [taskItems]);

  const validationSchema = dynamicValidationSchema(deserializeTaskItems(taskItems as any));

  const handleSubmit = async (values: TaskFormValuesType) => {
    setSubmitting(true);

    const token = await recaptchaRef.current.executeAsync();
    if (!token) {
      setSubmitting(false);
      return;
    }
    recaptchaRef.current.reset();

    // Build field value from a single field entry
    const buildFieldValue = (taskFieldId: string, value: any, groupInstanceId?: string) => {
      // Look in top-level items first
      let taskItem = taskItems.find(
        (item: any) => item.__typename === 'VOLUNTEER_TaskItemFieldType' && item.field?.taskFieldId === taskFieldId
      );
      // If not found, look inside repeatable groups
      if (!taskItem) {
        taskItems
          .filter((item: any) => item.__typename === 'VOLUNTEER_TaskItemRepeatableGroupType')
          .some((item: any) => {
            const child = (item.groupItems || []).find(
              (c: any) => c.__typename === 'VOLUNTEER_TaskItemFieldType' && c.field?.taskFieldId === taskFieldId
            );
            if (child) {
              taskItem = child;
              return true;
            }
            return false;
          });
      }
      if (!taskItem || (taskItem as any).__typename !== 'VOLUNTEER_TaskItemFieldType') {
        return null;
      }

      const fieldType = (taskItem as any).field.__typename;
      const isEmpty = (v: any) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

      let fieldValue: any = null;

      if (fieldType === 'VOLUNTEER_ShortTextTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_ShortTextTaskFieldValue', taskFieldId, shortTextValue: value };
      } else if (fieldType === 'VOLUNTEER_LongTextTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_LongTextTaskFieldValue', taskFieldId, longTextValue: value };
      } else if (fieldType === 'VOLUNTEER_NumberTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_NumberTaskFieldValue', taskFieldId, numericValue: value };
      } else if (fieldType === 'VOLUNTEER_EmailTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_EmailTaskFieldValue', taskFieldId, emailValue: value };
      } else if (fieldType === 'VOLUNTEER_PhoneTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_PhoneTaskFieldValue', taskFieldId, phoneValue: value };
      } else if (fieldType === 'VOLUNTEER_RatingTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_RatingTaskFieldValue', taskFieldId, ratingValue: value };
      } else if (fieldType === 'VOLUNTEER_AddressTaskFieldType') {
        const addressValue = value as any;
        if (!addressValue?.address?.placesAddress && !addressValue?.address?.manualAddress?.lineOne) return null;
        const address = addressValue?.addressOverride
          ? { manualAddress: addressValue.address?.manualAddress }
          : { placesAddress: addressValue?.address?.placesAddress };
        fieldValue = {
          __typename: 'VOLUNTEER_AddressTaskFieldValue',
          taskFieldId,
          addressOverride: addressValue?.addressOverride,
          address,
        };
      } else if (fieldType === 'VOLUNTEER_DateTaskFieldType') {
        if (!value) return null;
        fieldValue = { __typename: 'VOLUNTEER_DateTaskFieldValue', taskFieldId, dateValue: value };
      } else if (fieldType === 'VOLUNTEER_SignatureTaskFieldType') {
        if (!value) return null;
        fieldValue = { __typename: 'VOLUNTEER_SignatureTaskFieldValue', taskFieldId, signature: value };
      } else if (fieldType === 'VOLUNTEER_AttachmentTaskFieldType') {
        if (!value) return null;
        const attachmentField = value as any;
        const files = attachmentField?.addedAttachments || [];
        if (files.length === 0) return null;
        fieldValue = {
          __typename: 'VOLUNTEER_AttachmentTaskFieldValue',
          taskFieldId,
          pendingFiles: files,
          attachmentCreationTokens: files.map(() => uuidv4()),
          attachmentNames: files.map((f: any) => f.name || 'file'),
          removedAttachmentIds: attachmentField?.removedAttachmentIds || [],
        };
      } else if (fieldType === 'VOLUNTEER_DropdownTaskFieldType') {
        const ddValue = value as any;
        if (!ddValue?.dropdownOptionId) return null;
        fieldValue = {
          __typename: 'VOLUNTEER_DropdownTaskFieldValue',
          taskFieldId,
          dropdownTaskFieldOptionId: ddValue.dropdownOptionId,
          otherText: ddValue.otherOptionSelected ? ddValue.otherText : undefined,
        };
      } else if (fieldType === 'VOLUNTEER_CheckboxTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = {
          __typename: 'VOLUNTEER_CheckboxTaskFieldValue',
          taskFieldId,
          checkboxTaskFieldOptionIds: value,
        };
      } else if (fieldType === 'VOLUNTEER_MultipleChoiceTaskFieldType') {
        const mcValue = value as any;
        if (!mcValue?.multipleChoiceOptionId) return null;
        fieldValue = {
          __typename: 'VOLUNTEER_MultipleChoiceTaskFieldValue',
          taskFieldId,
          multipleChoiceTaskFieldOptionId: mcValue.multipleChoiceOptionId,
          multipleChoiceText: mcValue.multipleChoiceText,
        };
      } else if (fieldType === 'VOLUNTEER_QuizTaskFieldType') {
        const quizValue = value as any;
        if (!quizValue?.quizTaskFieldOptionId) return null;
        fieldValue = {
          __typename: 'VOLUNTEER_QuizTaskFieldValue',
          taskFieldId,
          quizTaskFieldOptionId: quizValue.quizTaskFieldOptionId,
          quizText: quizValue.quizText,
        };
      } else if (fieldType === 'VOLUNTEER_TimeTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_TimeTaskFieldValue', taskFieldId, timeValue: value };
      } else if (fieldType === 'VOLUNTEER_UrlTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_UrlTaskFieldValue', taskFieldId, urlValue: value };
      } else if (fieldType === 'VOLUNTEER_YesNoTaskFieldType') {
        if (value === null || value === undefined) return null;
        fieldValue = { __typename: 'VOLUNTEER_YesNoTaskFieldValue', taskFieldId, yesNoValue: value };
      } else if (fieldType === 'VOLUNTEER_LinearScaleTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_LinearScaleTaskFieldValue', taskFieldId, linearScaleValue: value };
      } else if (fieldType === 'VOLUNTEER_NpsTaskFieldType') {
        if (isEmpty(value)) return null;
        fieldValue = { __typename: 'VOLUNTEER_NpsTaskFieldValue', taskFieldId, npsValue: value };
      } else if (fieldType === 'VOLUNTEER_MatrixTaskFieldType') {
        if (isEmpty(value)) return null;
        const matrixEntries = Object.entries(value as Record<string, string>)
          .filter(([, columnId]) => columnId)
          .map(([rowId, columnId]) => ({ rowId, columnId }));
        if (matrixEntries.length === 0) return null;
        fieldValue = { __typename: 'VOLUNTEER_MatrixTaskFieldValue', taskFieldId, matrixEntries };
      } else {
        if (isEmpty(value)) return null;
        fieldValue = { taskFieldId, value };
      }

      if (fieldValue && groupInstanceId) {
        fieldValue.groupInstanceId = groupInstanceId;
      }

      return fieldValue;
    };

    const rawFieldValues = Object.entries(values)
      .flatMap(([key, value]) => {
        if (key.startsWith('__rg_')) {
          const groupData = (value as any) as RepeatableGroupFormikValue;
          if (!groupData?.instances) return [];
          return groupData.instances.flatMap((instance) =>
            Object.entries(instance.values)
              .map(([taskFieldId, fieldVal]) => buildFieldValue(taskFieldId, fieldVal, instance.groupInstanceId))
              .filter(Boolean)
          );
        }
        return buildFieldValue(key, value);
      })
      .filter(Boolean);

    const fileToBase64 = (file: any): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const result = reader.result?.toString() || '';
          resolve(result.substring(result.indexOf(',') + 1));
        };
        reader.onerror = (error) => reject(error);
      });

    const fieldValues = await Promise.all(
      rawFieldValues.map(async (fv: any) => {
        if (fv?.pendingFiles) {
          const addedAttachments = await Promise.all(fv.pendingFiles.map(fileToBase64));
          const { pendingFiles: _unused, ...rest } = fv;
          return { ...rest, addedAttachments };
        }
        return fv;
      })
    );

    const ipAddress = '';
    const { userAgent } = navigator;
    const { referrer } = document;

    try {
      const response = await submitFormFetch.run({
        formId: form.formId,
        fieldValues,
        ipAddress,
        userAgent,
        referrer,
        profileId: user?.profileId ?? null,
      });

      if (response.ok) {
        onSubmitSuccess();
      } else {
        const errorMessage = response.data?.message || 'Failed to submit form. Please try again.';
        setSnackbar({
          open: true,
          message: errorMessage,
          variant: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while submitting the form. Please try again.',
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!initialFormikValues) {
    return null;
  }

  return (
    <Box>
      {form.description && (
        <Box marginBottom={2} paddingLeft={1} paddingRight={1}>
          <Typography variant='body2' color='textSecondary' style={{ fontSize: '15px' }}>
            {form.description}
          </Typography>
        </Box>
      )}

      <TaskFieldContainerProvider container={FormFieldContainer}>
        <Box className={classes.formFieldsWrapper}>
          <Formik initialValues={initialFormikValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
            {({ values, isValid, dirty }) => {
              const isFormComplete = isValid && dirty;
              const isPaged = pages.length > 1;
              return (
                <Form>
                  <ReCAPTCHA ref={recaptchaRef} size='invisible' sitekey='6Lez7NsUAAAAAOTVaiFrSnXTvl4XPX7ivvVXzruA' />
                  <TaskFormFieldList
                    taskItems={deserializeTaskItems((isPaged ? currentPageItems : taskItems) as any)}
                    values={values}
                  />
                  <FormPageNav
                    pages={pages}
                    currentPage={currentPage}
                    submitting={submitting}
                    onBack={() => setCurrentPage((page) => Math.max(0, page - 1))}
                    onNext={() => setCurrentPage((page) => Math.min(pages.length - 1, page + 1))}
                    onGoToPage={setCurrentPage}
                  />
                  {!isPaged && (
                    <Box width={1} display='flex' justifyContent='flex-end' paddingTop={3} paddingRight={1}>
                      <TabletButton
                        color='primary'
                        variant='contained'
                        type='submit'
                        size='large'
                        style={{
                          borderRadius: 4,
                          minWidth: 120,
                          opacity: !isFormComplete || submitting ? 0.5 : 1,
                          cursor: !isFormComplete || submitting ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {submitting ? 'Submitting...' : 'Submit'}
                      </TabletButton>
                    </Box>
                  )}
                  <FormikErrorFocus />
                </Form>
              );
            }}
          </Formik>
        </Box>
      </TaskFieldContainerProvider>
    </Box>
  );
};
